import jwtDecode from 'jwt-decode';
import LocalStorageService from './LocalStorageService';
import { Amplify, Auth } from 'aws-amplify';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import axios from 'axios';
import TokensStorage from '../../userservice/common/util/TokensStorage';
import configs from '../../../conf';
import UserAPI from '../../userservice/apis/UserAPI';

const config = configs[process.env.APP_CONF];

class AuthorizationService {
  // @TODO rewrite the authenticate functionality to use Cognito
  /*static authenticate() {
    return axios
      .get('/auth/user')
      .then(response => {
        console.log('RES: ', response);
        LocalStorageService.setItem('userToken', response.data);
      })
      .catch(error => console.log(error));
  }*/

  static async sso(awsConfig) {
    Amplify.configure({
      Auth: {
        identityPoolId: awsConfig.IdentityPoolId,
        region: awsConfig.Region,
        userPoolId: awsConfig.UserPoolId,
        userPoolWebClientId: awsConfig.UserPoolClientAppId,
        storage: TokensStorage,
        oauth: {
          domain: `${awsConfig.SSODomain}.auth.${awsConfig.Region}.amazoncognito.com`, // WithOUT https:// protocol, e.g. "nicktestcompany-sso.auth.eu-west-1.amazoncognito.com"
          scope: ['aws.cognito.signin.user.admin', 'email', 'openid', 'profile'],
          redirectSignIn: `${config.domain}/login`,
          redirectSignOut: `${config.domain}/login`, // After signing OUT, we want to be redirected back to the login page, NOT the logout page, as we're already logged out
          responseType: 'code',
        },
      },
    });
    localStorage.setItem('configured', JSON.stringify(true));

    // Cache the AWS Config, since we will need it again if we have to re-login to work around the "Already found an entry" error for existing users
    localStorage.setItem('awsConfig', JSON.stringify(awsConfig));

    // Check which SAML provider to use
    const ssoFeature = awsConfig.companyFeatures?.filter(feature => feature.name === 'sso')[0];
    const ssoProvider = !ssoFeature?.value ? 'SAMLP' : ssoFeature.value;

    await Auth.federatedSignIn({ provider: ssoProvider }); // Redirects to SSO login page, then redirects back to web app login page with access code in URL params
  }

  static async finishSSOLogin(authResponse) {
    const idToken = jwtDecode(authResponse.id_token); //Decode the id token in order to get the expiration time
    const username = idToken['cognito:username'];
    const email = idToken.email;
    const exp = idToken.exp;
    const name = idToken.name ? idToken.name : null;

    const emailDomain = email.split('@').pop();

    let awsConfig = (awsConfig = await UserAPI.getAwsConfigForDomain(emailDomain, authResponse.id_token));

    const domain = 'cognito-idp.' + awsConfig.Region + '.amazonaws.com/' + awsConfig.UserPoolId;

    Amplify.configure({
      Auth: {
        identityPoolId: awsConfig.IdentityPoolId,
        region: awsConfig.Region,
        userPoolId: awsConfig.UserPoolId,
        userPoolWebClientId: awsConfig.UserPoolClientAppId,
        storage: TokensStorage,
      },
    });
    localStorage.setItem('configured', JSON.stringify(true));

    const credentials = await Auth.federatedSignIn(
      domain,
      {
        token: authResponse.id_token, // The id token returned from the SSO login
        expires_at: exp * 1000, // the expiration timestamp
      },
      {
        // User object
        username: username,
      }
    );

    // Set userDetails in localStorage
    localStorage.setItem(
      'userDetails',
      JSON.stringify({
        username: username,
        userPoolId: awsConfig.UserPoolId,
        appClientId: awsConfig.UserPoolClientAppId,
        identityPoolId: awsConfig.IdentityPoolId,
        userPoolWebClientId: awsConfig.UserPoolClientAppId,
        email: email,
        name: name,
        companyId: awsConfig.companyId,
        companyName: awsConfig.companyName,
        region: awsConfig.Region,
        bucket: awsConfig.InputBucketName,
        ssoDomain: awsConfig.SSODomain,
      })
    );

    return true;
  }

  static async exchangeAuthCodeForTokens(authResponse) {
    const code = authResponse.code;
    const awsConfig = JSON.parse(localStorage.getItem('awsConfig'));
    const token_endpoint = `https://${awsConfig.SSODomain}.auth.${awsConfig.Region}.amazoncognito.com/oauth2/token`;

    const state = sessionStorage.getItem('oauth_state');
    const code_verifier = sessionStorage.getItem('ouath_pkce_key'); // Mispelled by Amplify library... **facepalm**

    // Exchange code for our tokens
    if (authResponse.state !== state) {
      throw new Error(`Invalid state`);
    }

    const formData = new URLSearchParams();
    formData.append('grant_type', 'authorization_code');
    formData.append('client_id', awsConfig.UserPoolClientAppId);
    formData.append('code_verifier', code_verifier);
    formData.append('code', code);
    formData.append('redirect_uri', `${config.domain}/login`);

    const tokens = await fetch(token_endpoint, {
      method: 'POST',
      mode: 'cors',
      headers: {
        // TODO: if you have a client secret you can add this, but you should just not use a client secret on the frontend..
        // Authorization: `Basic ${btoa(`${clientId}:${clientSecret}`)}`,
        Accept: '*/*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    }).then(result => result.json());

    // Clean these up since we don't need them anymore
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('ouath_pkce_key');

    const idToken = jwtDecode(tokens.id_token); //Decode the id token in order to get the expiration time
    const username = idToken['cognito:username'];
    const email = idToken.email;
    const exp = idToken.exp;
    const name = idToken.name ? idToken.name : null;

    Amplify.configure({
      Auth: {
        identityPoolId: awsConfig.IdentityPoolId,
        region: awsConfig.Region,
        userPoolId: awsConfig.UserPoolId,
        userPoolWebClientId: awsConfig.UserPoolClientAppId,
        storage: TokensStorage,
      },
    });
    localStorage.setItem('configured', JSON.stringify(true));

    // create a CognitoAccessToken using the response accessToken
    const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({
      AccessToken: tokens['access_token'],
    });

    // create a CognitoIdToken using the tokensonse idToken
    const IdToken = new AmazonCognitoIdentity.CognitoIdToken({
      IdToken: tokens['id_token'],
    });

    // create a RefreshToken using the tokensonse refreshToken
    const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
      RefreshToken: tokens['refresh_token'],
    });

    // create a session object with all the tokens
    const sessionData = {
      IdToken: IdToken,
      AccessToken: AccessToken,
      RefreshToken: RefreshToken,
    };

    // create the CognitoUserSession using the sessionData
    const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);

    // create an object with the UserPoolId and ClientId
    var poolData = {
      UserPoolId: awsConfig.UserPoolId,
      ClientId: awsConfig.UserPoolClientAppId,
    };

    // pass the poolData object to CognitoUserPool
    var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);

    // create an object containing the username and user pool.
    // You can get the username from CognitoAccessToken object
    // we created above.
    var userData = {
      Username: AccessToken.payload.username,
      Pool: userPool,
    };

    console.log('userData', userData);
    console.log('AccessToken', AccessToken);

    // create a cognito user using the userData object
    var cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    // set the cognito user session w/ the CognitoUserSession
    cognitoUser.setSignInUserSession(session);

    // get the Amplify authenticated user
    Auth.currentAuthenticatedUser()
      .then(user => console.log('CURRENT USER', user))
      .catch(err => console.log(err));

    // Set userDetails in localStorage
    localStorage.setItem(
      'userDetails',
      JSON.stringify({
        username: username,
        userPoolId: awsConfig.UserPoolId,
        appClientId: awsConfig.UserPoolClientAppId,
        identityPoolId: awsConfig.IdentityPoolId,
        userPoolWebClientId: awsConfig.UserPoolClientAppId,
        email: email,
        name: name,
        companyId: awsConfig.companyId,
        companyName: awsConfig.companyName,
        region: awsConfig.Region,
        bucket: awsConfig.InputBucketName,
        ssoDomain: awsConfig.SSODomain,
      })
    );

    return true;
  }

  static async login(username, password) {
    const { data } = await axios.post('/login', { username });
    Amplify.configure({
      Auth: {
        identityPoolId: data.IdentityPoolId,
        region: data.Region,
        userPoolId: data.UserPoolId,

        // Required if userPoolId is defined? https://github.com/aws-amplify/amplify-js/issues/267
        userPoolWebClientId: data.UserPoolClientAppId,
        storage: TokensStorage,
      },
    });
    localStorage.setItem('configured', JSON.stringify(true));

    const user = await Auth.signIn(username, password);
    return {
      response: data,
      user,
    };
  }

  static async confirmLogin(cognitoUserFromSignIn, challengeAnswer, username) {
    const { data } = await axios.post('/login', { username });
    const loggedUser = await Auth.confirmSignIn(
      cognitoUserFromSignIn, // Return object from Auth.signIn()
      challengeAnswer, // Confirmation code
      'SOFTWARE_TOKEN_MFA' // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
    );
    return {
      response: data,
      user: loggedUser,
    };
  }

  static loggedIn() {
    try {
      const accessToken = LocalStorageService.getCognitoItem('accessToken');
      const decode = jwtDecode(accessToken);
      const username = decode.username;
      const expiry = decode.exp;

      if (!username) {
        return false;
      }

      return expiry >= Date.now() / 1000;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  static async logOut(desktopEmail = null) {
    // To do a global signout which also revokes the access token immediately,
    // we have to do something like this: https://github.com/aws-amplify/amplify-js/issues/3435#issuecomment-576178627

    const userDetails = LocalStorageService.getItem('userDetails');
    try {
      await Auth.signOut();
    } catch (error) {
      console.log('error signing out: ', error);
    } finally {
      LocalStorageService.removeAll();
    }
    const extensionId = process.env.EXT_ID;
    window.postMessage({ type: extensionId, message: 'LOGOUT_FROM_APP' }, '*');

    const isSsoUser = await this.isSsoUser();
    if (!isSsoUser || userDetails === undefined) {
      return;
    }

    let ssoDomain = userDetails?.ssoDomain + '.auth.' + userDetails?.region + '.amazoncognito.com';
    if (desktopEmail) {
      window.location = `https://${ssoDomain}/logout?client_id=${userDetails.appClientId}&logout_uri=${config.domain}/login?desktopEmail=${desktopEmail}`;
      return;
    }

    window.location = `https://${ssoDomain}/logout?client_id=${userDetails.appClientId}&logout_uri=${config.domain}/login`;
  }

  static async getCognitoAuthUser() {
    const userResponse = await Auth.currentAuthenticatedUser({ bypassCache: false });
    return userResponse;
  }

  static async isSsoUser() {
    let user = await this.getCognitoAuthUser();

    let idToken = null;

    if ('token' in user) {
      idToken = jwtDecode(user.token);
    } else if ('signInUserSession' in user) {
      idToken = jwtDecode(user.signInUserSession.idToken.jwtToken);
    } else {
      return false;
    }

    return 'identities' in idToken && idToken.identities.length > 0 && idToken.identities[0].providerType == 'SAML';
  }

  static async getPrefredMFA(authCognitoUser) {
    const getPrefredMFAResponse = await Auth.getPreferredMFA(authCognitoUser, { bypassCache: false });
    return getPrefredMFAResponse;
  }

  static async verifyTOTP(authCognitoUser, code) {
    const verifyResponse = await Auth.verifyTotpToken(authCognitoUser, code);
    return verifyResponse;
  }

  static async setPrefredMFA(authCognitoUser, mfaType) {
    const setPrefredMFAResponse = await Auth.setPreferredMFA(authCognitoUser, mfaType);
    return setPrefredMFAResponse;
  }
}

export default AuthorizationService;
